import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Option } from '@univacity/shared/types';
import { takeUntil } from 'rxjs';
import { AutoComplete } from '../../interfaces';
import { DestroyAbleComponent } from '../destroyable/destroyable.component';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

@Component({
  selector: 'un-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class PhoneInputComponent
  extends DestroyAbleComponent
  implements ControlValueAccessor, OnInit
{
  @Input() autoComplete: AutoComplete;
  @Input() disabled: boolean;
  @Input() formControlName?: string;
  @Input() formControl?: AbstractControl;
  @Input() placeholder?: string = `172 333 3310`;
  value?: string;
  form: FormGroup;
  options: Option[];
  loading: boolean;
  status: 'error' | '' = '';
  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    if (!this.placeholder) this.placeholder = $localize`e.g 172 333 3310`;
    this.search('');
    this.form = this.fb.group({
      phone: [''],
      prefix: ['+44'],
    });
    this.form.valueChanges.pipe(takeUntil(this.dead$)).subscribe((v) => {
      try {
        const num = phoneUtil.parse(`${v.prefix}${v.phone}`);
        this.value = phoneUtil.format(num, PhoneNumberFormat.E164);
        if (phoneUtil.isValidNumber(num)) {
          this.status = '';
          this.propagateChange(this.value);
        } else this.status = 'error';
      } catch (e) {
        this.status = 'error';
      }
    });
    this.cdr.detectChanges();
  }

  search(search: string): void {
    this.loading = true;
    this.autoComplete(search || '').subscribe({
      next: (res) => {
        this.options = res.options as Option[];
        this.cdr.detectChanges();
      },
      complete: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateChange = (_: any) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  propagateTouched = () => {};

  writeValue(v?: string): void {
    if (!v) return;
    try {
      const number = phoneUtil.parseAndKeepRawInput(v);
      const prefix = '+' + number.getCountryCode();
      const phone = number.getNationalNumber();
      this.form.patchValue({ prefix, phone });
      this.cdr.detectChanges();
    } catch (e) {
      console.info(e);
    }
  }
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
